import React from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';

/**
 * Gate for authenticated routes. Wrap routes that require a logged-in user:
 *
 *   <Route element={<ProtectedRoute />}>
 *     <Route path="/dashboard" element={<Dashboard />} />
 *   </Route>
 *
 * Redirects to /login — the app's built sign-in route (frontend/src/pages/login),
 * so the redirect always lands on a real page.
 *
 * THE CAPABILITY LOADING WINDOW. Between "we know there is a session" and "we
 * know what that session may open" there is a short window in which the app is
 * authenticated but its capabilities are unknown. Nothing authenticated renders
 * during it: this guard holds the whole authenticated subtree on the same
 * spinner it already used for the session itself. That is what stops
 * PermissionRoute from bouncing to /403 and the navbar from either hiding or
 * inventing links before the answer arrives.
 *
 * If the capability read FAILS (offline, server error) the window closes with
 * capabilities still unknown — `capabilitiesLoading` goes false and this guard
 * lets the subtree render, so `/` and `/change-password` (open to every
 * authenticated user, server-side too) still work, and PermissionRoute reports
 * the failure honestly for the pages that do need an answer.
 */
export default function ProtectedRoute() {
  const { isAuthenticated, loading, capabilitiesLoading } = useAuth();
  const location = useLocation();

  if (loading || (isAuthenticated && capabilitiesLoading)) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading…</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <Outlet />;
}
